'use client'

import ReactMarkdown, { type Components } from 'react-markdown'
import rehypeKatex from 'rehype-katex'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import 'katex/dist/katex.min.css'

import { escapeCurrency } from '@shared/markdown-currency'
import { escapeScientificAsterisks } from '@shared/markdown-scientific'

import { CopyableCodeBlock } from '@client/components/renderers/CopyableCodeBlock'

export { escapeCurrency } from '@shared/markdown-currency'

interface MarkdownRendererProps {
  content: string
  className?: string
  /** Give fenced code blocks a copy button (articles that hand the reader strings to paste). */
  copyableCode?: boolean
  /** Element overrides, merged over the defaults (e.g. an `img` that renders through next/image). */
  components?: Components
}

export function MarkdownRenderer({ content, className = '', copyableCode = false, components }: MarkdownRendererProps) {
  return (
    <div
      // `break-words` wraps long words/URLs; the arbitrary variants let wide blocks
      // (code, tables, display math) scroll within their own box instead of being
      // clipped by overflow-hidden ancestors (e.g. the mobile comment column).
      //
      // Inline `.katex` is constrained as well as `.katex-display`, as a floor
      // under the escaping above: a genuinely wide equation should scroll in
      // its own box, never widen the column it sits in. `min-w-0` is what makes
      // that work — without it a flex/grid child refuses to shrink below its
      // content and the overflow escapes to the ancestor anyway.
      className={`prose ${!className?.includes('prose-') ? 'prose-lg' : ''} min-w-0 max-w-none break-words [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_.katex-display]:max-w-full [&_.katex-display]:overflow-x-auto [&_.katex]:max-w-full [&_.katex]:overflow-x-auto [&_.katex]:align-middle ${className}`}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeRaw, rehypeKatex]}
        components={{ ...(copyableCode ? { pre: CopyableCodeBlock } : {}), ...components }}
      >
        {escapeScientificAsterisks(escapeCurrency(content.replace(/\\n/g, '\n')))}
      </ReactMarkdown>
    </div>
  )
}
